import { downloadSticker, useQrImage } from "@/lib/qr";
import { formatDate, formatPhone, type IncomingTransfer, type MyProperty } from "@elektr/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Download, FileDown, RefreshCw, Repeat } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { PassportPanel } from "@/components/ownership/PassportPanel";
import { TransferDialog } from "@/components/ownership/TransferDialog";
import { Button } from "@/components/ui/button";
import { Badge, Card, EmptyState, Spinner } from "@/components/ui/card";
import { Hero } from "@/components/ui/hero";
import { ConfirmDialog, Dialog } from "@/components/ui/dialog";
import { Field, Input, Select } from "@/components/ui/form";
import { useToast } from "@/components/ui/toast";
import { api, ApiError, errorMessage, unwrap } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { downloadBlob } from "@/lib/exportPng";
import { useT } from "@/lib/i18n";
import { STATUS_KEYS, STATUS_TONE, TYPE_KEYS } from "@/lib/labels";

export function MyHomesPage() {
  const { t } = useT();
  const { user } = useAuth();
  const toast = useToast();
  const homes = useQuery({
    queryKey: ["my-properties"],
    queryFn: async () => unwrap(await api.GET("/api/me/properties/")),
  });
  const transfers = useQuery({
    queryKey: ["my-transfers"],
    queryFn: async () => unwrap(await api.GET("/api/me/transfers/")),
  });
  const exportData = useMutation({
    mutationFn: async () => {
      const blob = unwrap(await api.GET("/api/me/export/", { parseAs: "blob" })) as unknown as Blob;
      downloadBlob(blob, "elektr-pasport-export.json");
    },
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });

  return (
    <>
      <Hero
        className="mb-4"
        title={t("dash.greeting", {
          name: (user?.full_name || "").split(" ")[0] || user?.phone || "",
        })}
        subtitle={t("my.hero_sub")}
      />
      <h2 className="mb-3 text-lg font-extrabold">{t("my.title")}</h2>
      {transfers.data?.incoming.map((tr) => (
        <IncomingTransferCard key={tr.id} transfer={tr} />
      ))}

      {homes.isLoading && <Spinner className="m-8 size-8" />}
      {homes.data && homes.data.length === 0 && (
        <EmptyState>
          <p className="mb-3">{t("my.empty")}</p>
          <Button asChild>
            <Link to="/my/claim">{t("my.nav_claim")}</Link>
          </Button>
        </EmptyState>
      )}
      <div className="flex flex-col gap-4">
        {homes.data?.map((h) => (
          <HomeCard key={h.id} home={h} />
        ))}
      </div>

      <Card className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold">{t("my.export_title")}</h3>
          <p className="text-sm text-muted-foreground">{t("my.export_hint")}</p>
        </div>
        <Button
          variant="outline"
          disabled={exportData.isPending}
          onClick={() => exportData.mutate()}
        >
          <FileDown className="size-4" /> {t("my.export")}
        </Button>
      </Card>
    </>
  );
}

function HomeCard({ home }: { home: MyProperty }) {
  const { t, lang: uiLang } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const qr = useQrImage(home.id, home.qr.token);
  const [stickerLang, setStickerLang] = useState<"uz" | "ru">(uiLang);
  const [confirm, setConfirm] = useState(false);
  const [transfer, setTransfer] = useState(false);
  const onError = (e: unknown) => toast.error(errorMessage(e, t("common.error")));

  const sticker = useMutation({
    mutationFn: (fmt: "pdf" | "png") => downloadSticker(home.id, fmt, stickerLang),
    onError,
  });
  const regenerate = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST("/api/properties/{id}/qr/regenerate/", {
          params: { path: { id: home.id } },
        }),
      ),
    onSuccess: () => {
      setConfirm(false);
      toast.success(t("qr.regenerated"));
      void qc.invalidateQueries({ queryKey: ["my-properties"] });
    },
    onError,
  });
  const cancelTransfer = useMutation({
    mutationFn: async () => {
      const res = await api.DELETE("/api/properties/{id}/transfer/", {
        params: { path: { id: home.id } },
      });
      if (!res.response.ok) throw new Error("cancel");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["my-properties"] }),
    onError,
  });
  const status = (home.status ?? "ready") as keyof typeof STATUS_KEYS;

  return (
    <Card className="flex flex-col gap-4" data-testid="home-card">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold">
            {home.address} {home.house_number}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t(TYPE_KEYS[home.type as keyof typeof TYPE_KEYS] ?? "type.other")} · {home.district}
            {home.installed_at && ` · ${t("props.installed_at")}: ${formatDate(home.installed_at)}`}
          </p>
        </div>
        <Badge tone={STATUS_TONE[status]}>{t(STATUS_KEYS[status])}</Badge>
      </div>

      <p className="text-sm">
        {t("props.company")}: <strong>{home.company.name}</strong>
        {home.company.phone && (
          <>
            {" · "}
            <a className="text-primary underline" href={`tel:${home.company.phone}`}>
              {formatPhone(home.company.phone)}
            </a>
          </>
        )}
      </p>

      {home.pending_transfer && (
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-primary/10 p-3 text-sm"
          role="status"
        >
          <span>
            {t("transfer.pending", {
              phone: formatPhone(home.pending_transfer.to_phone),
              date: formatDate(home.pending_transfer.expires_at),
            })}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={cancelTransfer.isPending}
            onClick={() => cancelTransfer.mutate()}
          >
            {t("transfer.cancel")}
          </Button>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-[13rem_minmax(0,1fr)]">
        <div className="flex flex-col items-center gap-2">
          {qr.data ? (
            <img src={qr.data} alt="QR" className="size-48 rounded-lg bg-white p-2" />
          ) : (
            <Spinner className="my-20 size-8" />
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              void navigator.clipboard?.writeText(home.qr.url);
              toast.success(t("qr.copied"));
            }}
          >
            <Copy className="size-4" /> {t("qr.copy")}
          </Button>
        </div>
        <div className="flex flex-col gap-3">
          <dl className="grid grid-cols-2 gap-2 text-center">
            <div className="rounded-lg bg-muted p-2">
              <dd className="text-xl font-bold">{home.qr.scans_total}</dd>
              <dt className="text-xs text-muted-foreground">{t("qr.scans_total")}</dt>
            </div>
            <div className="rounded-lg bg-muted p-2">
              <dd className="text-xl font-bold">{home.qr.scans_30_days}</dd>
              <dt className="text-xs text-muted-foreground">{t("qr.scans_30")}</dt>
            </div>
          </dl>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              aria-label={t("qr.sticker_lang")}
              className="w-32"
              value={stickerLang}
              onChange={(e) => setStickerLang(e.target.value as "uz" | "ru")}
            >
              <option value="uz">O‘zbekcha</option>
              <option value="ru">Русский</option>
            </Select>
            <Button size="sm" disabled={sticker.isPending} onClick={() => sticker.mutate("pdf")}>
              <Download className="size-4" /> {t("my.sticker")} PDF
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={sticker.isPending}
              onClick={() => sticker.mutate("png")}
            >
              <Download className="size-4" /> PNG
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setConfirm(true)}>
              <RefreshCw className="size-4" /> {t("qr.regenerate")}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setTransfer(true)}>
              <Repeat className="size-4" /> {t("transfer.start")}
            </Button>
          </div>
        </div>
      </div>

      <details className="rounded-lg border border-border p-3">
        <summary className="cursor-pointer font-semibold">{t("pass.title")}</summary>
        <div className="mt-3">
          <PassportPanel propertyId={home.id} />
        </div>
      </details>

      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title={t("qr.regenerate")}
        message={t("qr.regenerate_confirm")}
        confirmLabel={t("qr.regenerate")}
        pending={regenerate.isPending}
        onConfirm={() => regenerate.mutate()}
      />
      <TransferDialog propertyId={home.id} open={transfer} onOpenChange={setTransfer} />
    </Card>
  );
}

function IncomingTransferCard({ transfer }: { transfer: IncomingTransfer }) {
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const decline = useMutation({
    mutationFn: async () => {
      const res = await api.POST("/api/transfers/{id}/decline/", {
        params: { path: { id: transfer.id } },
      });
      if (!res.response.ok) throw new Error("decline");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["my-transfers"] }),
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });
  return (
    <Card
      className="mb-4 border-primary bg-primary/5"
      role="region"
      aria-label={t("transfer.incoming_title")}
    >
      <h3 className="mb-1 font-semibold">{t("transfer.incoming_title")}</h3>
      <p className="mb-3 text-sm">
        {t("transfer.incoming_desc", {
          who: transfer.from_name || "—",
          place: [transfer.district, transfer.region].filter(Boolean).join(", "),
          company: transfer.company_name,
          date: formatDate(transfer.expires_at),
        })}
      </p>
      <div className="flex gap-2">
        <Button onClick={() => setOpen(true)}>{t("transfer.accept")}</Button>
        <Button variant="outline" disabled={decline.isPending} onClick={() => decline.mutate()}>
          {t("transfer.decline")}
        </Button>
      </div>
      {open && <AcceptDialog transfer={transfer} onClose={() => setOpen(false)} />}
    </Card>
  );
}

/** Yangi egasi telefon raqamini OTP bilan qayta tasdiqlaydi: avval kod so'raladi, keyin kiritiladi. */
function AcceptDialog({ transfer, onClose }: { transfer: IncomingTransfer; onClose: () => void }) {
  const { t } = useT();
  const { user } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const sendCode = useMutation({
    mutationFn: async () =>
      unwrap(await api.POST("/api/auth/otp/request/", { body: { phone: user!.phone } })),
    onSuccess: () => {
      setSent(true);
      setError(null);
    },
    onError: (e) => setError(errorMessage(e, t("common.error"))),
  });
  const accept = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST("/api/transfers/{id}/accept/", {
          params: { path: { id: transfer.id } },
          body: { code },
        }),
      ),
    onSuccess: () => {
      toast.success(t("transfer.accepted"));
      void qc.invalidateQueries({ queryKey: ["my-properties"] });
      void qc.invalidateQueries({ queryKey: ["my-transfers"] });
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 410) return setError(t("transfer.expired"));
      setError(
        e instanceof ApiError && e.status === 400
          ? t("auth.code_invalid")
          : errorMessage(e, t("common.error")),
      );
    },
  });

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t("transfer.accept_title")}
      description={t("transfer.accept_desc", { phone: formatPhone(user?.phone) })}
    >
      <div className="flex flex-col gap-4">
        {!sent ? (
          <Button disabled={sendCode.isPending} onClick={() => sendCode.mutate()}>
            {t("auth.send_code")}
          </Button>
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              if (code.length === 6) accept.mutate();
            }}
          >
            <Field label={t("auth.code")} hint={t("auth.code_hint")}>
              {(id) => (
                <Input
                  id={id}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                />
              )}
            </Field>
            <Button type="submit" disabled={code.length !== 6 || accept.isPending}>
              {t("transfer.accept")}
            </Button>
          </form>
        )}
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
